import React, { useCallback, useEffect, useState } from "react";
import styles from "./Subscription.module.css";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import Pagination from "../pagination/Pagination";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getPromos,
  deletePromo,
  updatePromoStatus,
} from "../../api/HttpApis";
import PromoModal from "./PromoModal";

const formatDate = (d) =>
  d ? new Date(d).toISOString().slice(0, 10) : "—";

const Promos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("cards");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPromos({
        page: currentPage,
        limit: rowsPerPage,
        search,
      });
      const list = response?.promos || response?.data?.promos || [];
      const total =
        response?.pagination?.total ??
        response?.data?.pagination?.total ??
        list.length;
      setPromos(list);
      setTotalCount(total);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to load promos",
        "error"
      );
      setPromos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, search]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const handleAdd = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const handleEdit = (promo) => {
    setEditData(promo);
    setIsModalOpen(true);
  };

  const handleDelete = async (promo) => {
    if (
      !window.confirm(
        `Delete promo "${promo.name}"? This cannot be undone.`
      )
    )
      return;
    setDeletingId(promo.id);
    try {
      const response = await deletePromo(promo.id);
      showToast(response?.message || "Promo deleted", "success");
      fetchPromos();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to delete promo",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (promo) => {
    const next = promo.status === "Active" ? 0 : 1;
    try {
      const response = await updatePromoStatus(promo.id, next);
      showToast(response?.message || "Status updated", "success");
      fetchPromos();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to update status",
        "error"
      );
    }
  };

  const handleModalClose = (didChange) => {
    setIsModalOpen(false);
    setEditData(null);
    if (didChange) fetchPromos();
  };

  const renderCards = () => (
    <div className={styles["plans-grid"]}>
      {promos.length === 0 ? (
        <div className={styles["plan-card-empty"]}>
          No promos found. Click "+ Add Promo" to create one.
        </div>
      ) : (
        promos.map((promo) => (
          <div key={promo.id} className={styles["plan-card"]}>
            <div className={styles["plan-card-header"]}>
              <div>
                <h3 className={styles["plan-card-name"]}>{promo.name}</h3>
                {promo.description && (
                  <p className={styles["plan-card-desc"]}>
                    {promo.description}
                  </p>
                )}
              </div>
              <span
                className={`${styles.status} ${
                  promo.status === "Active" ? styles.active : styles.expired
                }`}
                onClick={() => handleToggleStatus(promo)}
                style={{ cursor: "pointer" }}
                title="Click to toggle status"
              >
                {promo.status}
              </span>
            </div>

            <div className={styles["plan-card-price"]}>
              <span className={styles["plan-card-price-amount"]}>
                {promo.currency || "USD"}{" "}
                {Number(promo.intro_price).toFixed(2)}
              </span>
              <span className={styles["plan-card-price-cycle"]}>
                / first {promo.duration_days} days
              </span>
            </div>

            <div className={styles["plan-card-specs"]}>
              <span className={styles["plan-card-spec"]}>
                Valid {formatDate(promo.valid_from)} → {formatDate(promo.valid_until)}
              </span>
              {promo.is_currently_active ? (
                <span className={styles["plan-card-spec"]}>Live now</span>
              ) : (
                <span className={styles["plan-card-spec"]}>Not active</span>
              )}
            </div>

            {Array.isArray(promo.applies_to_plans) &&
            promo.applies_to_plans.length > 0 ? (
              <ul className={styles["plan-card-features"]}>
                {promo.applies_to_plans.map((p) => (
                  <li key={p.id}>{p.name}</li>
                ))}
              </ul>
            ) : (
              <div className={styles["plan-card-empty-features"]}>
                Not linked to any plan
              </div>
            )}

            <div className={styles["plan-card-footer"]}>
              <span style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                {promo.subscribers_count ?? 0} subscriber
                {(promo.subscribers_count ?? 0) === 1 ? "" : "s"}
              </span>
              <div className={styles["plan-card-actions"]}>
                <Button variant="outline" onClick={() => handleEdit(promo)}>
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDelete(promo)}
                  loading={deletingId === promo.id}
                  disabled={deletingId === promo.id}
                >
                  {deletingId === promo.id ? "Removing..." : "Remove"}
                </Button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderTable = () => (
    <table className={styles["subscription-table"]}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Intro Price</th>
          <th>Duration</th>
          <th>Window</th>
          <th>Applies To</th>
          <th>Status</th>
          <th>Subscribers</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {promos.length === 0 ? (
          <tr>
            <td colSpan="8" style={{ textAlign: "center" }}>
              No promos found.
            </td>
          </tr>
        ) : (
          promos.map((promo) => (
            <tr key={promo.id}>
              <td>
                <div style={{ fontWeight: 600 }}>{promo.name}</div>
                {promo.description && (
                  <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                    {promo.description}
                  </div>
                )}
              </td>
              <td>
                {promo.currency} {Number(promo.intro_price).toFixed(2)}
              </td>
              <td>{promo.duration_days} days</td>
              <td>
                {formatDate(promo.valid_from)} → {formatDate(promo.valid_until)}
              </td>
              <td>
                {Array.isArray(promo.applies_to_plans) &&
                promo.applies_to_plans.length > 0
                  ? promo.applies_to_plans.map((p) => p.name).join(", ")
                  : "—"}
              </td>
              <td>
                <span
                  className={`${styles.status} ${
                    promo.status === "Active" ? styles.active : styles.expired
                  }`}
                  onClick={() => handleToggleStatus(promo)}
                  style={{ cursor: "pointer" }}
                  title="Click to toggle status"
                >
                  {promo.status}
                </span>
              </td>
              <td>{promo.subscribers_count ?? 0}</td>
              <td>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button variant="outline" onClick={() => handleEdit(promo)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDelete(promo)}
                    loading={deletingId === promo.id}
                    disabled={deletingId === promo.id}
                  >
                    {deletingId === promo.id ? "Removing..." : "Remove"}
                  </Button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className={styles["tab-content"]}>
      <div className={styles["plans-toolbar"]}>
        <div>
          <h2 className={styles["table-title"]}>Launch Promos & Offers</h2>
          <p className={styles["table-subtitle"]}>
            Time-bound intro pricing on selected plans
          </p>
        </div>

        <div className={styles["plans-toolbar-right"]}>
          <div className={styles["view-toggle"]}>
            <button
              type="button"
              className={view === "cards" ? styles.active : ""}
              onClick={() => setView("cards")}
            >
              Cards
            </button>
            <button
              type="button"
              className={view === "table" ? styles.active : ""}
              onClick={() => setView("table")}
            >
              Table
            </button>
          </div>

          <div style={{ width: "240px" }}>
            <InputText
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search promos..."
              variant="dark"
            />
          </div>

          <Button onClick={handleAdd}>+ Add Promo</Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
      ) : view === "cards" ? (
        renderCards()
      ) : (
        renderTable()
      )}

      {totalCount > rowsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          setRowsPerPage={setRowsPerPage}
          totalCount={totalCount}
          dataLength={promos.length}
          columnsLength={8}
          className={styles["history-pagination"]}
        />
      )}

      {isModalOpen && (
        <PromoModal editData={editData} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default Promos;
